import { useEffect } from 'react';

/**
 * Ported from the site's original inline <script> (the vanilla
 * "M.V.P. Trans-Logistics — front-end behaviour" block at the bottom of
 * index.html). Call this ONCE, at the top of <App/>, after all sections
 * have mounted.
 *
 * Deliberately NOT ported here (handled instead as real React state inside
 * <Header/>, which is both safer and more idiomatic):
 *   - header hide-on-scroll-down / show-on-scroll-up
 *   - mobile hamburger drawer
 *   - language dropdown
 *   - the Fancybox contact/thank-you modals — replaced by <ContactModal/>
 *     + ModalContext (React state), see shared/ModalContext.jsx
 *
 * Requires these packages to be installed:
 *   npm i gsap swiper lenis
 *
 * Everything is wrapped in try/catch exactly like the source, so one
 * missing selector/feature can't take down the rest of the page.
 */
export function useSiteBehavior() {
  useEffect(() => {
    let cleanupFns = [];
    let cancelled = false;

    (async () => {
      const gsapModule = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      const { default: Swiper } = await import('swiper');
      const { default: Lenis } = await import('lenis');
      await import('swiper/css');
      await import('swiper/css/effect-fade');
      await import('swiper/css/pagination');

      if (cancelled) return;

      const gsap = gsapModule.gsap || gsapModule.default || gsapModule;
      gsap.registerPlugin(ScrollTrigger);

      const isMobile = () => window.matchMedia('(max-width:1024px)').matches;

      /* 1) SMOOTH SCROLL (Lenis, synced to ScrollTrigger) */
      let lenis = null;
      try {
        lenis = new Lenis({
          duration: 1.15,
          easing: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
          smoothWheel: true,
          wheelMultiplier: 1,
          touchMultiplier: 1.2,
          prevent: (node) => !!(node.closest && node.closest('[data-smooth-scroll="ignore"]')),
        });
        lenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.add((time) => lenis.raf(time * 1000));
        gsap.ticker.lagSmoothing(0);
      } catch (err) {
        console.warn('Smooth scroll init failed, falling back to native scroll.', err);
        lenis = null;
      }

      function scrollToEl(target) {
        try {
          const el = typeof target === 'string' ? document.querySelector(target) : target;
          if (!el) return;
          if (lenis) lenis.scrollTo(el, { offset: 0 });
          else el.scrollIntoView({ behavior: 'smooth' });
        } catch (err) {
          console.warn(err);
        }
      }

      /* back-to-top / data-scroll-to="hero" */
      const scrollToBtns = [];
      try {
        document.querySelectorAll('[data-scroll-to]').forEach((btn) => {
          const handler = () => scrollToEl('#' + btn.getAttribute('data-scroll-to'));
          btn.addEventListener('click', handler);
          scrollToBtns.push([btn, handler]);
        });
      } catch (err) {
        console.warn(err);
      }

      /* generic in-page anchors (#contact etc.) */
      const anchorHandlers = [];
      try {
        document.querySelectorAll('a[href^="#"]').forEach((a) => {
          const href = a.getAttribute('href');
          if (!href || href.length < 2) return;
          const target = document.querySelector(href);
          if (!target) return;
          const handler = (e) => {
            e.preventDefault();
            scrollToEl(target);
          };
          a.addEventListener('click', handler);
          anchorHandlers.push([a, handler]);
        });
      } catch (err) {
        console.warn(err);
      }

      /* 3) LOAD-IN ENTRANCE — drives `body.is-loaded .hero-slider` in global.scss */
      const entranceTimer = setTimeout(() => {
        document.body.classList.add('is-loaded');
      }, 150);

      /* 4) HERO BANNER — Swiper autoplay slider ("Our services") */
      let heroSwiper = null;
      try {
        const root = document.querySelector('.hero-slider');
        if (root) {
          const counterCurrent = root.querySelector('.hero-slider__counter-current');

          function updateCounter(sw) {
            if (!counterCurrent) return;
            counterCurrent.textContent = String(sw.realIndex + 1).padStart(2, '0');
          }
          function replayTitle(sw) {
            try {
              const activeSlide = sw.slides[sw.activeIndex];
              if (!activeSlide) return;
              const chars = activeSlide.querySelectorAll('.animated-text .char');
              if (!chars.length) return;
              gsap.fromTo(
                chars,
                { scaleY: 0 },
                { scaleY: 1, duration: 0.5, stagger: 0.015, ease: 'power2.out', transformOrigin: '50% 0%' }
              );
            } catch (err) {
              console.warn(err);
            }
          }

          heroSwiper = new Swiper(root, {
            effect: 'fade',
            fadeEffect: { crossFade: true },
            loop: true,
            speed: 700,
            autoplay: { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true },
            pagination: { el: root.querySelector('.hero-slider__progressbar'), type: 'progressbar' },
            on: {
              init() {
                replayTitle(this);
                updateCounter(this);
              },
              slideChangeTransitionStart() {
                replayTitle(this);
                updateCounter(this);
              },
            },
          });
        }
      } catch (err) {
        console.warn('Hero banner init failed.', err);
      }

      /* 5) ADVANTAGES mobile carousel (Swiper, <=1024px only) */
      let advantagesSwiper = null;
      try {
        if (isMobile()) {
          const root = document.querySelector('.advantages__scrollbox');
          if (root) {
            advantagesSwiper = new Swiper(root, {
              slidesPerView: 1.08,
              spaceBetween: 16,
              pagination: { el: document.querySelector('.advantages__progressbar') || undefined, type: 'progressbar' },
            });
          }
        }
      } catch (err) {
        console.warn(err);
      }

      /* 6) SCROLL-REVEAL — "scale-block" / "opacity-block" cards (About + Contact) */
      try {
        document.querySelectorAll('.scale-block').forEach((el) => {
          gsap.fromTo(
            el,
            { scale: 0.82 },
            { scale: 1, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 92%', end: 'top 45%', scrub: 0.6 } }
          );
        });
        document.querySelectorAll('.opacity-block').forEach((el) => {
          gsap.fromTo(
            el,
            { opacity: 0 },
            { opacity: 1, ease: 'none', scrollTrigger: { trigger: el, start: 'top 92%', end: 'top 65%', scrub: 0.6 } }
          );
        });
      } catch (err) {
        console.warn('Card reveal animation failed.', err);
      }

      /* 7) ANIMATED HEADING TEXT — char-by-char reveal on scroll */
      try {
        document.querySelectorAll('.animated-text, .main-animated-text').forEach((heading) => {
          // Exclude space-only `.char` spans — animating scaleY on a bare
          // space collapses its rendered width on this font/line-height,
          // which is what glued "Your cargo is in reliable hands." into
          // "YOURCARGOISINRELIABLEHANDS." Only real, non-space characters
          // should be tweened.
          const chars = Array.from(heading.querySelectorAll('.char')).filter((el) => {
            const content = el.textContent;
            return content && content !== ' ' && content !== '\u00A0';
          });
          if (!chars.length) return;
          const isHero = heading.closest('.hero__head, .hero__container-inner') !== null;
          if (isHero) {
            gsap.fromTo(chars, { scaleY: 0 }, { scaleY: 1, duration: 0.7, stagger: 0.02, ease: 'power3.out', delay: 0.3 });
          } else {
            gsap.fromTo(chars, { scaleY: 0 }, {
              scaleY: 1, duration: 0.6, stagger: 0.02, ease: 'power3.out',
              scrollTrigger: { trigger: heading, start: 'top 88%' },
            });
          }
        });
      } catch (err) {
        console.warn('Heading reveal failed.', err);
      }

      /* 8) SERVICES — pinned stacking cards (desktop only, plain scroll-linked transform) */
      let servicesScrollHandler = null;
      let servicesResizeHandler = null;
      try {
        const scrollWrap = document.querySelector('.services-scroll');
        const items = document.querySelectorAll('.services__items .services__item');
        const fill = document.querySelector('.services__progressbar-fill');
        if (scrollWrap && items.length) {
          let ticking = false;
          const clamp01 = (n) => Math.max(0, Math.min(1, n));
          const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

          const update = () => {
            ticking = false;
            if (window.matchMedia('(max-width:1024px)').matches) return;

            const rect = scrollWrap.getBoundingClientRect();
            const total = rect.height - window.innerHeight;
            let progress = total > 0 ? -rect.top / total : 0;
            progress = clamp01(progress);

            const segment = 1 / (items.length - 1);
            items.forEach((item, i) => {
              // item-1 (i === 0) has no entrance — it's already centered
              // the moment the pin engages, so entry is locked at 1.
              // Every other card's window shifts back one slot to fill
              // the freed-up range.
              const entry = i === 0
                ? 1
                : easeOutCubic(clamp01((progress - (i - 1) * segment) / segment));

              const recede = i < items.length - 1
                ? easeOutCubic(clamp01((progress - i * segment) / segment))
                : 0;

              const y = 100 - entry * 150;
              const pxOffset = (1 - entry) * 60;
              const scaleVal = 1 - recede * 0.2;

              item.style.zIndex = String(i + 1);
              item.style.translate = 'none';
              item.style.rotate = 'none';
              item.style.scale = 'none';
              item.style.transform =
                `translate(-50%, ${y}%) translate3d(0px, ${pxOffset.toFixed(3)}px, 0px) scale(${scaleVal.toFixed(4)}, ${scaleVal.toFixed(4)})`;
            });

            if (fill) fill.style.width = `${10 + progress * 90}%`;
          };

          servicesScrollHandler = () => {
            if (!ticking) {
              requestAnimationFrame(update);
              ticking = true;
            }
          };
          servicesResizeHandler = update;
          window.addEventListener('scroll', servicesScrollHandler, { passive: true });
          window.addEventListener('resize', servicesResizeHandler);
          update();
        }
      } catch (err) {
        console.warn('Services stack failed.', err);
      }

      /* 9) ADVANTAGES — pinned stacking list (desktop only) */
      try {
        const section = document.querySelector('.advantages');
        const box = document.querySelector('.advantages__scrollbox');
        const inner = document.querySelector('.advantages__scrollbox-inner');
        const items = document.querySelectorAll('.advantages__scrollbox-item');
        const fill = document.querySelector('.advantages__progressbar-fill');

        if (section && box && inner && items.length >= 2) {
          ScrollTrigger.matchMedia({
            '(min-width: 1025px)': function () {
              const start = () => {
                const windowHeight = items[1].getBoundingClientRect().bottom - items[0].getBoundingClientRect().top;
                if (windowHeight <= 0 || windowHeight > 2000) {
                  console.warn('Advantages stack: implausible window height, skipping pin.', windowHeight);
                  return;
                }
                box.style.height = windowHeight + 'px';
                box.style.overflow = 'hidden';

                let travel = inner.scrollHeight - windowHeight;
                if (travel <= 0) return;

                // Safety clamp: this measurement depends on every item having
                // rendered at its real size already (images decoded, fonts
                // loaded, Swiper's `swiper-wrapper` CSS not fighting the
                // layout — see the accompanying style.scss fix). If any of
                // that isn't true yet, `travel` can come out absurdly large
                // and turn the page into thousands of pixels of dead scroll
                // space. Cap it rather than trust the raw measurement.
                const maxTravel = items.length * 900;
                if (travel > maxTravel) {
                  console.warn(
                    `Advantages stack: computed travel (${travel}px) looked too large, clamping to ${maxTravel}px. ` +
                    'This usually means items weren\'t laid out as an actual vertical stack when measured — check ' +
                    'that .advantages__scrollbox-inner isn\'t still using the swiper/css flex layout on desktop.'
                  );
                  travel = maxTravel;
                }

                const tl = gsap.timeline({
                  scrollTrigger: { trigger: section, start: 'top top', end: '+=' + travel, pin: true, scrub: true },
                });
                tl.to(inner, { y: -travel, ease: 'none' }, 0);
                if (fill) tl.to(fill, { width: '100%', ease: 'none' }, 0);
              };
              if (document.readyState === 'complete') start();
              else window.addEventListener('load', start);

              return () => {
                ScrollTrigger.getAll().forEach((st) => {
                  if (st.trigger === section) st.kill();
                });
                gsap.set(inner, { clearProps: 'all' });
                box.style.height = '';
                box.style.overflow = '';
              };
            },
          });
        }
      } catch (err) {
        console.warn('Advantages stack failed.', err);
      }

      /* refresh ScrollTrigger once everything above has run */
      try {
        window.addEventListener('load', () => ScrollTrigger.refresh());
        setTimeout(() => ScrollTrigger.refresh(), 600);
      } catch (err) {
        console.warn(err);
      }

      /* TEXT-HOVER — wave swap between elem-1 / elem-2 layers */
      const hoverCleanups = [];
      try {
        document.querySelectorAll('.text-hover').forEach((anchor) => {
          const elem1 = anchor.querySelector('.text-hover__elem-1');
          const elem2 = anchor.querySelector('.text-hover__elem-2');
          if (!elem1 || !elem2) return;

          const chars1 = elem1.querySelectorAll('.char');
          const chars2 = elem2.querySelectorAll('.char');

          gsap.set(chars1, { yPercent: 0 });
          gsap.set(chars2, { yPercent: 100 });

          const tl = gsap.timeline({ paused: true });
          tl.to(chars1, { yPercent: -100, stagger: 0.025, duration: 0.45, ease: 'power3.out' }, 0);
          tl.to(chars2, { yPercent: 0, stagger: 0.025, duration: 0.45, ease: 'power3.out' }, 0);

          const onEnter = () => tl.play();
          const onLeave = () => tl.reverse();
          anchor.addEventListener('mouseenter', onEnter);
          anchor.addEventListener('mouseleave', onLeave);
          hoverCleanups.push(() => {
            anchor.removeEventListener('mouseenter', onEnter);
            anchor.removeEventListener('mouseleave', onLeave);
          });
        });
      } catch (err) {
        console.warn(err);
      }

      cleanupFns = [
        () => clearTimeout(entranceTimer),
        () => heroSwiper && heroSwiper.destroy(true, true),
        () => advantagesSwiper && advantagesSwiper.destroy(true, true),
        () => servicesScrollHandler && window.removeEventListener('scroll', servicesScrollHandler),
        () => servicesResizeHandler && window.removeEventListener('resize', servicesResizeHandler),
        () => ScrollTrigger.getAll().forEach((st) => st.kill()),
        () => lenis && lenis.destroy(),
        ...scrollToBtns.map(([btn, handler]) => () => btn.removeEventListener('click', handler)),
        ...anchorHandlers.map(([a, handler]) => () => a.removeEventListener('click', handler)),
        ...hoverCleanups,
      ];
    })();

    return () => {
      cancelled = true;
      cleanupFns.forEach((fn) => {
        try {
          fn();
        } catch (err) {
          // ignore cleanup errors
        }
      });
    };
  }, []);
}
